import {
    createLayout as createLayoutInternal,
    type PanelData,
    type Component,
    type GridLayout,
} from "svelte-editor-layout";

import { persisted } from "svelte-persisted-store";

import Icon from "$lib/utility/Icon.svelte";
import Palette from "$lib/panels/Palette.svelte";
import { Log, LogHeader } from "@ceyper-common";
import { COMMANDS, LOGGER } from "$lib/commands";
import Tools from "$lib/panels/Tools.svelte";
import Layers from "$lib/panels/Layers.svelte";
import Files from "$lib/panels/Files.svelte";
import Frames from "$lib/panels/Frames.svelte";
import History from "$lib/panels/History.svelte";
import PaletteHeader from "$lib/panels/PaletteHeader.svelte";
import ToolOptions from "$lib/panels/ToolOptions.svelte";
import ColorPicker from "$lib/panels/ColorPicker.svelte";

function icon(id: string) {
    return {
        component: Icon,
        props: { id, size: 1.5 },
    } as Component;
}

export function createPanels() {
    const panels = [
        {
            name: "Palette",
            id: "palette",
            icon: icon("palette"),
            initialLocation: "right-upper",
            initialActive: true,
            component: Palette,
            props: {},
            templateOptions: {
                header: {
                    component: PaletteHeader,
                    props: {},
                },
            },
        },
        {
            name: "Color picker",
            id: "color-picker",
            icon: icon("color-picker"),
            initialLocation: "right-upper",
            initialActive: true,
            component: ColorPicker,
            props: {},
            templateOptions: {},
        },
        {
            name: "Layers",
            id: "layers",
            icon: icon("layers"),
            initialLocation: "right-lower",
            initialActive: true,
            component: Layers,
            props: {},
        },
        {
            name: "Files",
            id: "files",
            icon: icon("files"),
            initialLocation: "bottom-right",
            initialActive: false,
            component: Files,
            props: {},
        },
        {
            name: "Log viewer",
            id: "log",
            icon: icon("log"),
            initialLocation: "bottom-right",
            initialActive: false,
            component: Log,
            props: {
                logger: LOGGER,
            },
            templateOptions: {
                header: {
                    component: LogHeader,
                    props: {
                        logger: LOGGER,
                    },
                },
            },
        },
        {
            name: "Tools",
            id: "tools",
            icon: icon("tools"),
            initialLocation: "left-upper",
            initialActive: true,
            component: Tools,
            props: {},
            templateOptions: {
                header: {
                    enabled: false,
                },
                scroll: false,
            },
        },
        {
            name: "Tool options",
            id: "tool-options",
            icon: icon("tools"),
            initialLocation: "right-upper",
            initialActive: false,
            component: ToolOptions,
            props: {},
            templateOptions: {},
        },
        {
            name: "Frames",
            id: "frames",
            icon: icon("frames"),
            initialLocation: "bottom-right",
            initialActive: true,
            component: Frames,
            props: {},
            templateOptions: {
                header: {
                    enabled: false,
                },
                scroll: false,
            },
        },
        {
            name: "History",
            id: "history",
            icon: icon("history"),
            initialLocation: "bottom-right",
            initialActive: false,
            component: History,
            props: {},
            templateOptions: {},
        },
    ] satisfies PanelData[];
    return panels as PanelData[];
}

export function createLayout() {
    const layout = createLayoutInternal(
        createPanels(),
        [
            {
                id: "editor-grid",
                type: "grid",
                areas: {
                    rows: [
                        { areas: ["menu", "menu", "menu"] },
                        { areas: ["tools", "content", "panel-right"], min: 256 },
                        { areas: ["panel-bottom", "panel-bottom", "panel-right"], min: 24 },
                    ],
                    columns: [{}, { min: 256 }, { min: 128, initial: 20 }],
                },
                fill: "content",
                resize: ["panel-right", "panel-bottom"],
            },
            {
                id: "panel-right",
                type: "split",
                direction: "vertical",
                initial: ["palette", "color-picker", "layers"],
                options: {},
            },
            {
                id: "panel-bottom",
                type: "tab",
                initial: ["frames", "files", "log"],
                initialOpen: "frames",
                position: "bottom",
                options: {},
            },
        ],
        {
            state: persisted("editor-layout", undefined as any),
            options: {
                panelBars: {
                    left: {
                        visible: false,
                    },
                    right: {
                        visible: true,
                    },
                },
                panels: {
                    left: {
                        size: "3rem",
                    },
                },
            },
        }
    );

    COMMANDS.registerAction("view.reset-layout", layout.reset, "all");

    return layout;
}

export function createLayoutMobile() {
    const layout = createLayoutInternal(
        createPanels(),
        [
            {
                id: "editor-grid",
                type: "grid",
                areas: {
                    rows: [
                        { areas: ["menu", "menu", "menu"] },
                        { areas: ["tools", "content", "content"], min: 256 },
                        { areas: ["panel-bottom", "panel-bottom", "panel-bottom"], min: 24 },
                    ],
                    columns: [{}, { min: 256 }, { min: 128, initial: 20 }],
                },
                fill: "content",
                resize: ["panel-bottom"],
            },
            {
                id: "panel-bottom",
                type: "tab",
                initial: ["palette", "color-picker", "layers", "frames", "files"],
                initialOpen: "layers",
                position: "bottom",
                options: {},
            },
        ],
        {
            state: persisted("editor-layout", undefined as any),
            options: {
                panelBars: {
                    left: {
                        visible: false,
                    },
                    right: {
                        visible: true,
                    },
                },
                panels: {
                    left: {
                        size: "3rem",
                    },
                },
            },
        }
    );

    COMMANDS.registerAction("view.reset-layout", layout.reset, "all");

    return layout;
}
